import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { concatenateSelectedCurrencyForPDF, isBrowser, isIbanNumber } from '../../helper/helpers';
import {
  MUI_COLORS,
  INVOICE_FONTS,
  EXTERNAL_PAYMENT_LINKS,
  INVOICE_PAYMENT_PDF_LINK,
  INVOICE_STATUS,
  SESSION_STORAGE_NAME,
  REDIRECTION_SOURCE,
  EVENT_NAME
} from '../../constants/enum';
import DownloadIcon from '../../images/icons/download-icon.svg';
import PrimaryButton from '../ui/buttons/PrimaryButton';
import { convertDateToDDMMYYYY } from '../../helper/helpers';
import QRCodeGenerator from '../common/QRCodeGenerator';
import PropTypes from 'prop-types';

const blobToBase64DataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

/**
 * Gatsby SSR uses the Node build of `@react-pdf/renderer`, where `usePDF` throws.
 * The library is loaded only in the client; the factory below creates the real UI once it is available.
 */
function DownloadInvoiceAsPDF(props) {
  const [Impl, setImpl] = useState(null);
  const safeProps = props ?? {};

  useEffect(() => {
    let cancelled = false;
    import('@react-pdf/renderer').then((pdf) => {
      if (!cancelled) {
        // setImpl(component) is wrong: React treats a function arg as a state updater. Return the component from the updater.
        setImpl(() => createDownloadInvoiceComponent(pdf));
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const { caption, bgColor, color, fontSize, className, type, insideClass, isIcon } = safeProps;

  if (!Impl) {
    return (
      <PrimaryButton
        type={type}
        isLoading
        className={className}
        id="download-invoice-btn"
        bgColor={bgColor}
        color={color}
        fontSize={fontSize}
        caption={
          <div className={`flex justify-center gap-2 items-center ${insideClass}`}>
            {isIcon && <img src={DownloadIcon} alt="downloadIcon" />}
            {caption}
          </div>
        }
        onClick={() => { }}
      />
    );
  }

  return <Impl {...safeProps} />;
}

function createDownloadInvoiceComponent(pdf) {
  const { Page, Text, View, Document, StyleSheet, Image, Font, usePDF, Link } = pdf;

  const normalizePaymentHref = (rawPaymentUrl) => {
    if (!rawPaymentUrl || typeof rawPaymentUrl !== 'string') return null;
    const trimmedPaymentUrl = rawPaymentUrl.trim();
    if (!trimmedPaymentUrl) return null;
    if (INVOICE_PAYMENT_PDF_LINK.HAS_URI_SCHEME.test(trimmedPaymentUrl)) return trimmedPaymentUrl;
    return `${INVOICE_PAYMENT_PDF_LINK.DEFAULT_SCHEME_PREFIX}${trimmedPaymentUrl}`;
  };

  const insertSoftBreaksForPdfWrap = (paymentUrlPlainText) => {
    if (!paymentUrlPlainText || typeof paymentUrlPlainText !== 'string') return paymentUrlPlainText;
    const {
      SOFT_BREAK_AFTER_CHARS: delimiterBasedSoftBreakPattern,
      ZERO_WIDTH_SPACE: invisibleLineWrapCharacter,
      MAX_RUN_BEFORE_SOFT_BREAK: maxUrlCharactersBeforeSoftLineBreak
    } = INVOICE_PAYMENT_PDF_LINK;
    const paymentUrlWithDelimiterBreaks = paymentUrlPlainText.replace(
      delimiterBasedSoftBreakPattern,
      `$1${invisibleLineWrapCharacter}`
    );
    let paymentUrlWithWrapOpportunities = '';
    let charactersSinceLastSoftBreak = 0;
    for (let charIndex = 0; charIndex < paymentUrlWithDelimiterBreaks.length; charIndex += 1) {
      const currentCharacter = paymentUrlWithDelimiterBreaks[charIndex];
      paymentUrlWithWrapOpportunities += currentCharacter;
      if (currentCharacter === invisibleLineWrapCharacter) {
        charactersSinceLastSoftBreak = 0;
      } else {
        charactersSinceLastSoftBreak += 1;
        if (charactersSinceLastSoftBreak >= maxUrlCharactersBeforeSoftLineBreak) {
          paymentUrlWithWrapOpportunities += invisibleLineWrapCharacter;
          charactersSinceLastSoftBreak = 0;
        }
      }
    }
    return paymentUrlWithWrapOpportunities;
  };

  Font.register({
    family: 'HKGrotesk-Regular',
    src: INVOICE_FONTS?.HKGROTESK_REGULAR
  });

  Font.register({
    family: 'HKGrotesk-Bold',
    src: INVOICE_FONTS?.HKGROTESK_BOLD
  });

  Font.register({
    family: 'Microsoft',
    src: INVOICE_FONTS?.MICROSOFT
  });
  Font.register({
    family: 'Noto-Sans',
    src: INVOICE_FONTS?.GOOGLE_NOTO
  });

  const styles = StyleSheet.create({
    page: {
      paddingHorizontal: '1.4cm',
      paddingVertical: '1.2cm',
      fontSize: '0.37cm',
      fontFamily: ['HKGrotesk-Regular', 'Microsoft', 'Noto-Sans'],
      color: MUI_COLORS.MID_NIGHT,
      height: '100%',
      letterSpacing: '-0.01cm'
    },
    invoiceImage: {
      maxWidth: '100%',
      maxHeight: '2cm',
      flexDirection: 'row',
      justifyContent: 'start'
    },
    paidImage: {
      maxWidth: '3.6cm',
      maxHeight: '2cm',
      flexDirection: 'row',
      justifyContent: 'start',
      objectFit: 'contain'
    },
    invoicecImage: {
      objectFit: 'contain',
      objectPosition: 'left'
    },
    fromAndToContainer: {
      width: '100%',
      flexDirection: 'row',
      justifyContent: 'between',
      paddingVertical: '0.63cm'
    },
    section: {
      flexGrow: 1,
      fontSize: '0.37cm',
      flex: 1
    },
    fromAndTo: {
      flexDirection: 'row',
      textTransform: 'capitalize'
    },
    invoiceBilledTo: {
      color: MUI_COLORS.DARK_GRAYISH_BLUE,
      fontSize: '0.42cm'
    },
    title: {
      fontSize: '0.84cm',
      fontFamily: ['HKGrotesk-Bold', 'Microsoft', 'Noto-Sans'], //bold
      textTransform: 'capitalize',
      letterSpacing: '-0.02cm'
    },
    invoiceTitle: {},
    flexContainer: {
      flexDirection: 'row'
    },
    invoiceTitleAndDetails: {
      width: '40%',
      alignItems: 'flex-end',
      fontSize: '0.37cm'
    },
    invoiceTitleContainer: {
      marginTop: '0.37cm',
      flexGrow: 1
    },
    invoiceDetails: {
      color: MUI_COLORS.DARK_GRAYISH_BLUE,
      marginTop: '0.21cm',
      flexDirection: 'row'
    },
    invoiceTotalAmount: {
      fontFamily: ['HKGrotesk-Bold', 'Microsoft', 'Noto-Sans'], //bold
      letterSpacing: '-0.02cm'
    },
    baseTextColor: {
      color: MUI_COLORS.MID_NIGHT
    },
    invoiceDetailsTitle: {
      fontFamily: ['HKGrotesk-Bold', 'Microsoft', 'Noto-Sans'], //bold
      fontWeight: '700'
    },
    invoiceDescriptionHeader: {
      fontFamily: ['HKGrotesk-Bold', 'Microsoft', 'Noto-Sans'], //bold
      color: MUI_COLORS.DARK_GRAYISH_BLUE,
      fontSize: '0.47cm',
      borderBottom: '0.02cm',
      borderBottomColor: MUI_COLORS.DARK_GRAYISH_BLUE,
      paddingBottom: '0.21cm'
    },
    itemAndTaskContainer: {
      flexDirection: 'row',
      width: '100%',
      paddingVertical: '0.63cm',
      borderBottom: '0.02cm',
      borderBottomColor: MUI_COLORS.DARK_GRAYISH_BLUE
    },
    taskSection: {
      flexGrow: 0.4,
      flex: 0.4,
      flexShrink: 1,
      minWidth: 0,
      maxWidth: '40%',
      paddingRight: '0.32cm'
    },
    itemsSection: {
      width: '42%',
      flexGrow: 0,
      flexShrink: 0,
      paddingRight: '0.42cm'
    },
    descriptionSection: {
      flexGrow: 0.6,
      flex: 0.6,
      flexShrink: 1,
      minWidth: 0,
      marginHorizontal: '0.69cm',
      paddingRight: '0.21cm'
    },
    priceSection: {
      flexGrow: 1,
      flex: 1,
      flexDirection: 'row',
      justifyContent: 'between'
    },
    itemsPriceSection: {
      width: '58%',
      flexGrow: 0,
      flexShrink: 0,
      flexDirection: 'row',
      justifyContent: 'between'
    },
    quantityColumn: {
      width: '22%',
      flexGrow: 0,
      flexShrink: 0,
      alignItems: 'center',
      paddingHorizontal: '0.16cm'
    },
    goodsQuantityColumn: {
      flexGrow: 0.7,
      flex: 0.7,
      flexShrink: 0,
      alignItems: 'center',
      paddingHorizontal: '0.16cm'
    },
    itemDescriptionText: {
      width: '100%'
    },
    taskContainer: {
      marginTop: '0.63cm',
      flexDirection: 'row',
      width: '100%',
      paddingVertical: '0.63cm',
      borderBottom: '0.02cm',
      borderBottomColor: MUI_COLORS.DARK_GRAYISH_BLUE,
      color: MUI_COLORS.DARK_GRAYISH_BLUE
    },
    summaryContainer: {
      width: '100%',
      flexDirection: 'row',
      justifyContent: 'between',
      marginTop: '0.63cm'
    },
    textLight: {
      color: MUI_COLORS.DARK_GRAYISH_BLUE
    },
    invoiceItem: {
      marginHorizontal: '0.37cm',
      textAlign: 'center',
      flexGrow: 1,
      flex: 1
    },
    paymentDetailsContainer: {
      backgroundColor: MUI_COLORS.EXTRA_LIGHT_GRAY,
      padding: '0.42cm',
      borderRadius: '0.21cm',
      width: '100%',
      minWidth: 0,
      alignSelf: 'stretch'
    },
    bankDetailsColumn: {
      marginBottom: '0.21cm',
      minWidth: '3.81cm'
    },
    QRImage: {
      width: '1.4cm',
      height: '1.4cm',
      marginRight: '0.31cm'
    },
    wordBreak: {
      flex: 1
    },
    paymentLinkColumn: {
      flex: 1,
      flexGrow: 1,
      flexShrink: 1,
      flexBasis: 0,
      minWidth: 0,
      maxWidth: '100%'
    },
    paymentDetailsRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      width: '100%',
      minWidth: 0
    },
    paymentLinkTextWrap: {
      minWidth: 0,
      maxWidth: '100%',
      width: '100%'
    },
    paymentLinkAnchor: {
      color: MUI_COLORS.CORAL,
      textDecoration: 'none',
      minWidth: 0,
      maxWidth: '100%'
    },
    invoiceTitleAndPaid: {
      flexDirection: 'row',
      alignItems: 'center'
    },
    paidContainer: {
      backgroundColor: MUI_COLORS.GREEN,
      borderRadius: '0.191cm',
      marginRight: '0.2cm'
    },
    paidText: {
      padding: '0.211cm',
      color: MUI_COLORS.WHITE,
      fontFamily: ['HKGrotesk-Bold', 'Microsoft', 'Noto-Sans'], //bold
      letterSpacing: '0.0132cm'
    }
  });

  const PaymentPdfUrlLine = ({ paymentUrlPlainText }) => {
    const absolutePaymentLinkHref = normalizePaymentHref(paymentUrlPlainText);
    return (
      <View style={styles.paymentLinkTextWrap}>
        <Text style={[styles.invoiceDetailsTitle, styles.paymentLinkTextWrap]} wrap>
          {absolutePaymentLinkHref ? (
            <Link
              src={absolutePaymentLinkHref}
              style={[styles.invoiceDetailsTitle, styles.paymentLinkAnchor, styles.paymentLinkTextWrap]}
              wrap
            >
              {insertSoftBreaksForPdfWrap(paymentUrlPlainText)}
            </Link>
          ) : (
            insertSoftBreaksForPdfWrap(paymentUrlPlainText)
          )}
        </Text>
      </View>
    );
  };

  const InvoiceAsPDF = ({ getInvoiceDetails }) => (
    <Document>
      <Page wrap={false} style={styles.page}>
        <RenderPdfContent getInvoiceDetails={getInvoiceDetails} />
      </Page>
    </Document>
  );
  const InvoiceAsA4PDF = ({ getInvoiceDetails, isViewInvoice }) => (
    <Document>
      <Page size="A4" style={styles.page}>
        <RenderPdfContent getInvoiceDetails={getInvoiceDetails} isViewInvoice={isViewInvoice} />
      </Page>
    </Document>
  );

  const RenderPdfContent = ({ getInvoiceDetails, isViewInvoice = false }) => {
    return (
      <>
        <View style={styles.fromAndToContainer}>
          <View
            style={[
              styles.section,
              {
                marginRight: '1.05cm'
              }
            ]}
          >
            <View style={styles.invoiceImage}>
              {getInvoiceDetails?.logoPublic && (
                <Image
                  style={styles.invoicecImage}
                  src={getInvoiceDetails?.logoPublic + '?noCache=' + Math.random().toString()}
                />
              )}
            </View>
            <View style={[{ marginTop: '0.62cm' }]}>
              <Text
                style={[
                  !getInvoiceDetails?.companyDetails?.companyName && styles.invoiceDetailsTitle,
                  { textTransform: 'capitalize' }
                ]}
              >
                {getInvoiceDetails?.companyDetails?.firstName} {getInvoiceDetails?.companyDetails?.lastName}
              </Text>
              <Text style={[styles.invoiceDetailsTitle, { textTransform: 'capitalize' }]}>
                {getInvoiceDetails?.companyDetails?.companyName && (
                  <Text>{getInvoiceDetails?.companyDetails?.companyName}</Text>
                )}
              </Text>
              {getInvoiceDetails?.companyDetails?.registrationNumber && (
                <Text># {getInvoiceDetails?.companyDetails?.registrationNumber}</Text>
              )}
              {(getInvoiceDetails?.companyDetails?.address ||
                getInvoiceDetails?.companyDetails?.city ||
                getInvoiceDetails?.companyDetails?.state ||
                getInvoiceDetails?.companyDetails?.postalCode) && (
                  <View style={styles.fromAndTo}>
                    <Text>
                      {[
                        getInvoiceDetails?.companyDetails?.address,
                        getInvoiceDetails?.companyDetails?.city,
                        getInvoiceDetails?.companyDetails?.state,
                        getInvoiceDetails?.companyDetails?.postalCode
                      ]
                        .filter(Boolean)
                        .join(', ')}
                    </Text>
                  </View>
                )}
              {getInvoiceDetails?.companyDetails?.country && (
                <Text style={{ textTransform: 'uppercase' }}>{getInvoiceDetails?.companyDetails?.country}</Text>
              )}
              {getInvoiceDetails?.companyDetails?.website && <Text>{getInvoiceDetails?.companyDetails?.website}</Text>}
              {getInvoiceDetails?.companyDetails?.email && <Text>{getInvoiceDetails?.companyDetails?.email}</Text>}
            </View>
            <View style={[{ marginTop: '0.62cm' }]}>
              <Text style={styles.invoiceBilledTo}>Billed to</Text>
            </View>
            <View style={[{ marginTop: '0.21cm' }]}>
              <Text style={[styles.invoiceDetailsTitle, { textTransform: 'capitalize' }]}>
                {getInvoiceDetails?.contact?.companyName && getInvoiceDetails?.contact?.companyName}
              </Text>
              {getInvoiceDetails?.contact?.registrationNumber && (
                <Text># {getInvoiceDetails?.contact?.registrationNumber}</Text>
              )}

              {(getInvoiceDetails?.contact?.address ||
                getInvoiceDetails?.contact?.city ||
                getInvoiceDetails?.contact?.state ||
                getInvoiceDetails?.contact?.postalCode) && (
                  <View style={styles.fromAndTo}>
                    <Text>
                      {[
                        getInvoiceDetails?.contact?.address,
                        getInvoiceDetails?.contact?.city,
                        getInvoiceDetails?.contact?.state,
                        getInvoiceDetails?.contact?.postalCode
                      ]
                        .filter(Boolean)
                        .join(', ')}
                    </Text>
                  </View>
                )}
              {getInvoiceDetails?.contact?.country && (
                <Text style={{ textTransform: 'uppercase' }}>{getInvoiceDetails?.contact?.country}</Text>
              )}
              {getInvoiceDetails?.contact?.website && <Text>{getInvoiceDetails?.contact?.website}</Text>}
              <Text
                style={[
                  !getInvoiceDetails?.contact?.companyName && styles.invoiceDetailsTitle,
                  { textTransform: 'capitalize', marginTop: '0.62cm' }
                ]}
              >
                {getInvoiceDetails?.contact?.firstName} {getInvoiceDetails?.contact?.lastName}
              </Text>
              {getInvoiceDetails?.contact?.email && <Text>{getInvoiceDetails?.contact?.email}</Text>}
            </View>
          </View>

          <View style={styles.invoiceTitleAndDetails}>
            <View style={styles.invoiceTitleAndPaid}>
              {getInvoiceDetails?.status === INVOICE_STATUS.INVOICE_PAID && (
                <View style={styles.paidContainer}>
                  <Text style={styles.paidText}>Paid</Text>
                </View>
              )}
              <Text style={styles.title}>Invoice</Text>
            </View>
            <View style={styles.invoiceDetails}>
              <Text>Invoice Number: </Text> <Text style={styles.baseTextColor}> {getInvoiceDetails?.invoiceNumber}</Text>
            </View>
            <View style={[styles.invoiceDetails, { marginTop: '0.42cm' }]}>
              <Text>Date: </Text>{' '}
              <Text style={styles.baseTextColor}> {convertDateToDDMMYYYY(getInvoiceDetails?.createDate)}</Text>
            </View>
            {getInvoiceDetails?.payments?.[0]?.paidDate ? (
              <View style={[styles.invoiceDetails, { marginTop: '0.42cm' }]}>
                <Text>Paid at: </Text>{' '}
                <Text style={styles.baseTextColor}>
                  {' '}
                  {convertDateToDDMMYYYY(getInvoiceDetails?.payments?.[0]?.paidDate)}
                </Text>
              </View>
            ) : (
              <View style={[styles.invoiceDetails, { marginTop: '0.42cm' }]}>
                <Text>Due: </Text>{' '}
                <Text style={styles.baseTextColor}> {convertDateToDDMMYYYY(getInvoiceDetails?.dueDate)}</Text>
              </View>
            )}
            <View style={[styles.invoiceDetails, { marginTop: '0.42cm' }]}>
              <Text>Invoice amount: </Text>{' '}
              <Text style={styles.baseTextColor}>
                {concatenateSelectedCurrencyForPDF(getInvoiceDetails?.currency, getInvoiceDetails?.totalAmount)}
              </Text>
            </View>
            <View style={[styles.invoiceDetails, { marginTop: '0.42cm' }]}>
              <Text>
                {getInvoiceDetails?.otherReferences && (
                  <Text style={styles.baseTextColor}>{getInvoiceDetails?.otherReferences}</Text>
                )}
              </Text>
            </View>
          </View>
        </View>

        <View>
          <Text style={styles.invoiceDescriptionHeader}>Description</Text>
        </View>
        {getInvoiceDetails?.services?.length > 0 && (
          <View style={[styles.itemAndTaskContainer, { color: MUI_COLORS.DARK_GRAYISH_BLUE }]}>
            <View style={styles.taskSection}>
              <Text>Items</Text>
            </View>
            <View style={styles.descriptionSection}>
              <Text>Description</Text>
            </View>
            <View style={styles.priceSection}>
              <View style={styles.invoiceItem}>
                <Text style={{ textAlign: 'left' }}>Rate</Text>
              </View>
              <View>
                <Text>Hours</Text>
              </View>
              <View style={{ flexGrow: 1, flex: 1, textAlign: 'right' }}>
                <Text style={{ textAlign: 'right' }}>Total</Text>
              </View>
            </View>
          </View>
        )}
        {getInvoiceDetails?.services
          ? getInvoiceDetails?.services?.map((taskDetails, index) => {
            return (
              <View key={`services-item-${index}`} style={styles.itemAndTaskContainer}>
                <View style={styles.taskSection}>
                  <Text>{taskDetails?.taskTitle}</Text>
                </View>
                <View style={styles.descriptionSection}>
                  <Text>{taskDetails?.taskDescription}</Text>
                </View>
                <View style={styles.priceSection}>
                  <View style={styles.invoiceItem}>
                    <Text style={{ textAlign: 'left' }}>
                      {concatenateSelectedCurrencyForPDF(getInvoiceDetails?.currency, taskDetails?.taskRate)}
                    </Text>
                  </View>
                  <View>
                    <Text>{taskDetails?.taskHours}</Text>
                  </View>
                  <View style={{ flexGrow: 1, flex: 1, textAlign: 'right' }}>
                    <Text style={{ textAlign: 'right' }}>
                      {concatenateSelectedCurrencyForPDF(
                        getInvoiceDetails?.currency,
                        taskDetails?.taskRate * taskDetails?.taskHours
                      )}
                    </Text>
                  </View>
                </View>
              </View>
            );
          })
          : ''}
        {getInvoiceDetails?.goods?.length > 0 && (
          <View style={[styles.taskContainer, { marginTop: '0.63cm', color: MUI_COLORS.DARK_GRAYISH_BLUE }]}>
            <View style={styles.taskSection}>
              <Text>Items</Text>
            </View>
            <View style={styles.descriptionSection}>
              <Text>Description</Text>
            </View>
            <View style={styles.priceSection}>
              <View style={styles.goodsQuantityColumn}>
                <Text style={{ textAlign: 'center' }}>Qty</Text>
              </View>
              <View style={styles.invoiceItem}>
                <Text style={{ textAlign: 'center' }}>Price</Text>
              </View>
              <View style={{ flexGrow: 1, flex: 1, textAlign: 'right' }}>
                <Text style={{ textAlign: 'right' }}>Total</Text>
              </View>
            </View>
          </View>
        )}

        {getInvoiceDetails?.goods
          ? getInvoiceDetails?.goods?.map((itemDetails, index) => {
            return (
              <View key={`goods-item-${index}`} style={styles.itemAndTaskContainer}>
                <View style={styles.taskSection}>
                  <Text style={styles.itemDescriptionText}>{itemDetails?.itemTitle}</Text>
                </View>
                <View style={styles.descriptionSection}>
                  <Text style={styles.itemDescriptionText}>{itemDetails?.itemDescription}</Text>
                </View>
                <View style={styles.priceSection}>
                  <View style={styles.goodsQuantityColumn}>
                    <Text style={{ textAlign: 'center' }}>{itemDetails?.itemQuantity}</Text>
                  </View>
                  <View style={styles.invoiceItem}>
                    <Text style={{ textAlign: 'center' }}>
                      {concatenateSelectedCurrencyForPDF(getInvoiceDetails?.currency, itemDetails?.itemPrice)}
                    </Text>
                  </View>
                  <View style={{ flexGrow: 1, flex: 1, textAlign: 'right' }}>
                    <Text style={{ textAlign: 'right' }}>
                      {concatenateSelectedCurrencyForPDF(
                        getInvoiceDetails?.currency,
                        itemDetails?.itemQuantity * itemDetails?.itemPrice
                      )}
                    </Text>
                  </View>
                </View>
              </View>
            );
          })
          : ''}

        {/* NEW INVOICE ITEMS */}

        {getInvoiceDetails?.items?.length > 0 && (
          <View style={[styles.itemAndTaskContainer, { color: MUI_COLORS.DARK_GRAYISH_BLUE }]}>
            <View style={styles.itemsSection}>
              <Text>Items</Text>
            </View>
            <View style={styles.itemsPriceSection}>
              <View style={styles.quantityColumn}>
                <Text style={{ textAlign: 'center' }}>Quantity</Text>
              </View>
              <View style={{ flexGrow: 0.7, flex: 0.7 }}>
                <Text style={{ textAlign: 'left' }}>Unit</Text>
              </View>
              <View style={styles.invoiceItem}>
                <Text style={{ textAlign: 'left' }}>Price</Text>
              </View>
              <View style={{ flexGrow: 1, flex: 1, textAlign: 'right' }}>
                <Text style={{ textAlign: 'right' }}>Total</Text>
              </View>
            </View>
          </View>
        )}
        {getInvoiceDetails?.items
          ? getInvoiceDetails?.items?.map((itemDetails, index) => {
            return (
              <View key={`item-detail-${index}`} style={styles.itemAndTaskContainer}>
                <View style={styles.itemsSection}>
                  <Text style={styles.itemDescriptionText}>{itemDetails?.itemTitle}</Text>
                  <Text style={styles.itemDescriptionText}>{itemDetails?.itemDescription}</Text>
                </View>

                <View style={styles.itemsPriceSection}>
                  <View style={styles.quantityColumn}>
                    <Text style={{ textAlign: 'center' }}>{itemDetails?.itemQuantity}</Text>
                  </View>
                  <View style={{ flexGrow: 0.7, flex: 0.7 }}>
                    <Text style={{ textAlign: 'left' }}>{itemDetails?.unit.name}</Text>
                  </View>
                  <View style={styles.invoiceItem}>
                    <Text style={{ textAlign: 'left' }}>
                      {concatenateSelectedCurrencyForPDF(getInvoiceDetails?.currency, itemDetails?.itemPriceRate)}
                    </Text>
                  </View>
                  <View style={{ flexGrow: 1, flex: 1, textAlign: 'right' }}>
                    <Text style={{ textAlign: 'right' }}>
                      {concatenateSelectedCurrencyForPDF(getInvoiceDetails?.currency, itemDetails?.itemTotal)}
                    </Text>
                  </View>
                </View>
              </View>
            );
          })
          : ''}
        <View style={styles.summaryContainer}>
          <View
            style={[
              styles.section,
              { width: '60%', maxWidth: '60%', minWidth: 0, flexShrink: 1, marginRight: '0.84cm' }
            ]}
          >
            {getInvoiceDetails?.paymentDetails && (
              <Text style={[styles.textLight, { marginBottom: '0.26cm' }]}>Payment Details</Text>
            )}
            {getInvoiceDetails?.paymentDetails?.bankDetails
              ? getInvoiceDetails?.paymentDetails?.bankDetails?.map((bankData, index) => (
                <View
                  wrap={isViewInvoice ? false : true}
                  style={[styles.paymentDetailsContainer, { marginTop: '0.21cm' }]}
                  key={index + 1}
                >
                  <View style={!isViewInvoice && { flex: 1, flexGrow: 1 }}>
                    {bankData?.bankCountry && (
                      <View style={{ flexDirection: 'row', marginBottom: '0.10cm' }}>
                        <Text style={styles.bankDetailsColumn}>Bank country:</Text>
                        <Text>{bankData?.bankCountry}</Text>
                      </View>
                    )}
                    {bankData?.bankAddress && (
                      <View style={{ flexDirection: 'row', marginBottom: '0.10cm' }}>
                        <Text style={styles.bankDetailsColumn}>Bank address:</Text>
                        <Text style={styles.wordBreak}>{bankData?.bankAddress}</Text>
                      </View>
                    )}
                    {bankData?.fullName && (
                      <View style={{ flexDirection: 'row', marginBottom: '0.10cm' }}>
                        <Text style={styles.bankDetailsColumn}>Full Name:</Text>
                        <Text style={styles.wordBreak}>{bankData?.fullName}</Text>
                      </View>
                    )}
                    {bankData?.bankName && (
                      <View style={{ flexDirection: 'row', marginBottom: '0.10cm' }}>
                        <Text style={styles.bankDetailsColumn}>Bank name:</Text>
                        <Text style={styles.wordBreak}>{bankData?.bankName}</Text>
                      </View>
                    )}
                    {bankData?.accountNumberOrIban && (
                      <View style={{ flexDirection: 'row', marginBottom: '0.10cm' }}>
                        <Text style={styles.bankDetailsColumn}>{isIbanNumber(bankData?.accountNumberOrIban)}</Text>
                        <Text style={styles.wordBreak}>{bankData?.accountNumberOrIban}</Text>
                      </View>
                    )}
                    {bankData?.accountNumberOrIban && (
                      <View style={{ flexDirection: 'row', marginBottom: '0.10cm' }}>
                        <Text style={styles.bankDetailsColumn}>SWIFT/BIC code:</Text>
                        <Text style={styles.wordBreak}>{bankData?.swiftCode}</Text>
                      </View>
                    )}
                    {bankData?.routingCode && (
                      <View style={{ flexDirection: 'row', marginBottom: '0.10cm' }}>
                        <Text style={styles.bankDetailsColumn}>Routing Code:</Text>
                        <Text style={styles.wordBreak}>{bankData?.routingCode}</Text>
                      </View>
                    )}
                  </View>
                  <View>
                    <View>
                      {bankData?.bankCode && (
                        <View style={{ flexDirection: 'row', marginRight: '0.84cm', minWidth: '4cm' }}>
                          <Text style={styles.bankDetailsColumn}>Bank code:</Text>
                          <Text style={styles.wordBreak}>{bankData?.bankCode}</Text>
                        </View>
                      )}
                      {bankData?.branchCode && (
                        <View style={{ flexDirection: 'row', marginRight: '0.84cm', minWidth: '4cm' }}>
                          <Text style={styles.bankDetailsColumn}>Branch code:</Text>
                          <Text>{bankData?.branchCode}</Text>
                        </View>
                      )}
                    </View>
                  </View>
                </View>
              ))
              : ''}
            {getInvoiceDetails?.paymentDetails?.paypalDetails?.isSelected && (
              <View style={styles.paymentDetailsContainer}>
                <View style={styles.paymentDetailsRow}>
                  {getInvoiceDetails?.paymentDetails?.paypalDetails?.showQrCode && (
                    <Image
                      allowDangerousPaths
                      src={document.getElementById('paypal#QR')?.toDataURL()}
                      style={styles.QRImage}
                    />
                  )}
                  <View style={styles.paymentLinkColumn}>
                    <Text>Pay with PayPal</Text>
                    <PaymentPdfUrlLine
                      paymentUrlPlainText={`${EXTERNAL_PAYMENT_LINKS.PAYPAL_LINK}${getInvoiceDetails?.paymentDetails?.paypalDetails?.payPalLink
                        }${getInvoiceDetails?.totalAmount
                          ? `/${getInvoiceDetails?.totalAmount}${getInvoiceDetails?.currency?.name}`
                          : ''
                        }`}
                    />
                  </View>
                </View>
              </View>
            )}
            {getInvoiceDetails?.paymentDetails?.hsbcDetails?.isSelected && (
              <View style={[styles.paymentDetailsContainer, { marginTop: '0.21cm' }]}>
                <View style={styles.paymentDetailsRow}>
                  {getInvoiceDetails?.paymentDetails?.hsbcDetails?.showQrCode && (
                    <Image
                      allowDangerousPaths
                      src={document.getElementById('hsbc#QR')?.toDataURL()}
                      style={styles.QRImage}
                    />
                  )}
                  <View style={styles.paymentLinkColumn}>
                    <Text>Pay with PayMe</Text>
                    <PaymentPdfUrlLine
                      paymentUrlPlainText={`${EXTERNAL_PAYMENT_LINKS.PAYME_HSBC_LINK}${getInvoiceDetails?.paymentDetails?.hsbcDetails?.hsbcLink}`}
                    />
                  </View>
                </View>
              </View>
            )}
            {getInvoiceDetails?.paymentDetails?.customPaymentDetails?.isSelected && (
              <View style={[styles.paymentDetailsContainer, { marginTop: '0.21cm' }]}>
                <View style={styles.paymentDetailsRow}>
                  <View style={styles.paymentLinkColumn}>
                    <Text>{getInvoiceDetails?.paymentDetails?.customPaymentDetails?.customLabel}</Text>
                    <PaymentPdfUrlLine
                      paymentUrlPlainText={getInvoiceDetails?.paymentDetails?.customPaymentDetails?.customLink}
                    />
                  </View>
                </View>
              </View>
            )}
            {getInvoiceDetails?.paymentDetails?.customTextDetails?.isSelected && (
              <View style={[styles.paymentDetailsContainer, { marginTop: '0.21cm' }]}>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <View>
                    <Text>{getInvoiceDetails?.paymentDetails?.customTextDetails?.customText}</Text>
                  </View>
                </View>
              </View>
            )}
            {getInvoiceDetails?.notes ? (
              <Text style={[styles.textLight, { marginTop: '0.68cm', marginBottom: '0.15cm' }]}>Notes</Text>
            ) : (
              ''
            )}
            <Text style={styles.wordBreak}>{getInvoiceDetails?.notes}</Text>
          </View>
          <View style={[styles.section, { width: '35%', marginTop: '0.63cm' }]}>
            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                width: '100%',
                marginBottom: '0.63cm'
              }}
            >
              <View style={{ width: '2.11cm' }}>
                <Text>Subtotal</Text>
              </View>
              <Text>{concatenateSelectedCurrencyForPDF(getInvoiceDetails?.currency, getInvoiceDetails?.subtotal)}</Text>
            </View>
            {getInvoiceDetails?.invoiceDiscount?.discountPercentage &&
              getInvoiceDetails?.invoiceDiscount?.discountPercentage !== '0' && (
                <View
                  style={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    marginBottom: '0.63cm'
                  }}
                >
                  <Text>{getInvoiceDetails?.invoiceDiscount?.discountName}</Text>
                  <Text>{getInvoiceDetails?.invoiceDiscount?.discountPercentage}%</Text>
                  <Text>
                    -
                    {concatenateSelectedCurrencyForPDF(
                      getInvoiceDetails?.currency,
                      getInvoiceDetails?.invoiceDiscount?.discountPrice
                    )}
                  </Text>
                </View>
              )}

            {getInvoiceDetails?.taxWithPrice
              ? getInvoiceDetails?.taxWithPrice?.map((taxDetails, index) => {
                return (
                  <View
                    key={`taxWithPrice-${index}`}
                    style={{
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      marginBottom: '0.63cm'
                    }}
                  >
                    <Text>{taxDetails?.taxConcept}</Text>
                    <Text>{taxDetails?.taxPercentage}%</Text>

                    <Text>
                      {' '}
                      {Number(taxDetails?.taxPrice) < 0
                        ? `-${concatenateSelectedCurrencyForPDF(
                          getInvoiceDetails?.currency,
                          Math.abs(taxDetails?.taxPrice)
                        )}`
                        : `${concatenateSelectedCurrencyForPDF(getInvoiceDetails?.currency, taxDetails?.taxPrice)}`}
                    </Text>
                  </View>
                );
              })
              : ''}
            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                width: '100%',
                fontFamily: 'HKGrotesk-Regular', //bold
                fontSize: '0.42cm'
              }}
            >
              <Text style={styles.invoiceTotalAmount}>Total {`(${getInvoiceDetails?.currency?.name})`}</Text>
              <Text style={styles.invoiceTotalAmount}>
                {concatenateSelectedCurrencyForPDF(getInvoiceDetails?.currency, getInvoiceDetails?.totalAmount)}
              </Text>
            </View>
          </View>
        </View>
      </>
    );
  };

  function DownloadInvoiceAsPDFInner(rawProps) {
    const {
      caption,
      getInvoiceDetails,
      isIcon,
      bgColor,
      fontSize,
      id,
      color,
      className,
      type,
      insideClass,
      isViewInvoice = false
    } = rawProps ?? {};
    const [isQrGenerated, setIsQrGenerated] = useState();
    const [isLoading, setIsLoading] = useState(false);
    const [isClient, setIsClient] = useState(false);
    const [downloadPending, setDownloadPending] = useState(false);
    const isMobileApp =
      isBrowser() && sessionStorage.getItem(SESSION_STORAGE_NAME.REDIRECTION_SOURCE) === REDIRECTION_SOURCE.MOBILE_APP;

    const pdfDownloadLinkRef = useRef();

    const pdfDocument = useMemo(() => {
      if (!getInvoiceDetails) return null;
      const invoiceKey = getInvoiceDetails._id || getInvoiceDetails.invoiceNumber;
      if (!invoiceKey) return null;

      const detailsForPdf = isViewInvoice
        ? {
          ...getInvoiceDetails,
          paymentDetails: {
            bankDetails: getInvoiceDetails?.paymentDetails?.bankDetails ?? [],
            ...getInvoiceDetails?.paymentDetails
          }
        }
        : getInvoiceDetails;

      return isViewInvoice ? (
        <InvoiceAsA4PDF
          key={invoiceKey}
          getInvoiceDetails={detailsForPdf}
          isViewInvoice={isViewInvoice}
        />
      ) : (
        <InvoiceAsPDF key={invoiceKey} getInvoiceDetails={detailsForPdf} />
      );
    }, [getInvoiceDetails, isViewInvoice]);

    const [pdfState, updatePdf] = usePDF();

    useEffect(() => {
      setIsClient(true);
    }, []);

    useEffect(() => {
      if (!isClient || !pdfDocument) return;
      updatePdf(pdfDocument);
    }, [isClient, pdfDocument, updatePdf]);

    const runMobilePdfShare = useCallback(
      async (blobUrl) => {
        try {
          const response = await fetch(blobUrl);
          const blob = await response.blob();
          const base64link = await blobToBase64DataUrl(blob);
          const pdfDataUri = `data:application/pdf;base64,${base64link}`;

          const data = {
            event: EVENT_NAME.DOWNLOAD_DOCUMENTS,
            data: {
              url: pdfDataUri,
              fileName: `${getInvoiceDetails?.invoiceNumber}.pdf`
            }
          };

          if (isBrowser()) {
            window.ReactNativeWebView.postMessage(JSON.stringify(data));
          }
        } catch (error) {
          console.error('Error while downloading or converting PDF to base64', error);
        } finally {
          setIsLoading(false);
        }
      },
      [getInvoiceDetails?.invoiceNumber]
    );

    const triggerDownload = () => {
      if (pdfState.error) {
        setIsLoading(false);
        return;
      }
      if (!pdfState.url || pdfState.loading) {
        setDownloadPending(true);
        return;
      }
      if (isMobileApp) {
        void runMobilePdfShare(pdfState.url);
      } else {
        pdfDownloadLinkRef.current?.click();
        setIsLoading(false);
      }
    };

    useEffect(() => {
      if (!downloadPending) return;
      if (pdfState.loading || !pdfState.url || pdfState.error) {
        if (pdfState.error) {
          setDownloadPending(false);
          setIsLoading(false);
        }
        return;
      }
      setDownloadPending(false);
      if (isMobileApp) {
        void runMobilePdfShare(pdfState.url);
      } else {
        pdfDownloadLinkRef.current?.click();
        setIsLoading(false);
      }
    }, [downloadPending, pdfState.loading, pdfState.url, pdfState.error, isMobileApp, runMobilePdfShare]);

    const handleDownloadInvoice = () => {
      setIsLoading(true);
      triggerDownload();
    };

    const generateQRCode = () => {
      setIsQrGenerated(true);
    };

    return (
      <>
        <PrimaryButton
          type={type}
          isLoading={isLoading}
          className={className}
          id="download-invoice-btn"
          bgColor={bgColor}
          color={color}
          fontSize={fontSize}
          caption={
            <div className={`flex justify-center gap-2 items-center ${insideClass}`}>
              {isIcon && <img src={DownloadIcon} alt="downloadIcon" />}
              {caption}
            </div>
          }
          onClick={handleDownloadInvoice}
        />
        <div className="hidden">
          {isClient && pdfDocument && (
            <a
              ref={pdfDownloadLinkRef}
              href={pdfState.url || undefined}
              download={getInvoiceDetails?.invoiceNumber}
              aria-hidden
            />
          )}
          {isQrGenerated ? (
            <>
              <QRCodeGenerator
                valueString={`${EXTERNAL_PAYMENT_LINKS.PAYPAL_LINK}${getInvoiceDetails?.paymentDetails?.paypalDetails?.payPalLink
                  }${getInvoiceDetails?.totalAmount
                    ? `/${getInvoiceDetails?.totalAmount}${getInvoiceDetails?.currency?.name}`
                    : ''
                  }`}
                documentId="paypal#QR"
              />
              <QRCodeGenerator
                valueString={`${EXTERNAL_PAYMENT_LINKS.PAYME_HSBC_LINK}${getInvoiceDetails?.paymentDetails?.hsbcDetails?.hsbcLink}`}
                documentId="hsbc#QR"
              />
            </>
          ) : (
            <>
              <QRCodeGenerator
                valueString={`${EXTERNAL_PAYMENT_LINKS.PAYPAL_LINK}${getInvoiceDetails?.paymentDetails?.paypalDetails?.payPalLink
                  }${getInvoiceDetails?.totalAmount
                    ? `/${getInvoiceDetails?.totalAmount}${getInvoiceDetails?.currency?.name}`
                    : ''
                  }`}
                documentId="paypal#QR"
                onLoad={generateQRCode}
              />
              <QRCodeGenerator
                valueString={`${EXTERNAL_PAYMENT_LINKS.PAYME_HSBC_LINK}${getInvoiceDetails?.paymentDetails?.hsbcDetails?.hsbcLink}`}
                documentId="hsbc#QR"
                onLoad={generateQRCode}
              />
            </>
          )}
        </div>
      </>
    );
  }

  return DownloadInvoiceAsPDFInner;
}

export default DownloadInvoiceAsPDF;

DownloadInvoiceAsPDF.propTypes = {
  bgColor: PropTypes.string,
  color: PropTypes.string,
  className: PropTypes.string,
  fontSize: PropTypes.string,
  type: PropTypes.string,
  isIcon: PropTypes.bool,
  insideClass: PropTypes.string
};

DownloadInvoiceAsPDF.defaultProps = {
  isIcon: true,
  bgColor: 'bg-white',
  color: 'text-coral-500',
  fontSize: 'text-lg',
  className: 'get-paid-invoice-download-button w-full',
  type: 'small',
  insideClass: 'text-medium'
};
